import { IoChevronBack } from "react-icons/io5";
import { FaPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import ReusableProfileCard from "../Profile/ReusableProfileCard";
import { BsInfoCircle } from "react-icons/bs";
import {
  MdOutlineColorLens,
  MdOutlinePrivacyTip,
  MdOutlineStarBorderPurple500,
} from "react-icons/md";
import { RxShare2 } from "react-icons/rx";
import { RiUserUnfollowLine } from "react-icons/ri";
import { HiOutlineLogout } from "react-icons/hi";
import LogoutModal from "./LogOutModal";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import BlockUserListModal from "./BlockUserListModal";
import { RWebShare } from "react-web-share";
import { CiLock } from "react-icons/ci";
import TextTranslate from "../../../utils/TextTranslate";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { HiLanguage } from "react-icons/hi2";
import ThemeToggleFromSettings from "../ThemeToggleFromSettings";
import NotificationToggleFromSettings from "../NotificationToggleFromSettings";
import SubscriptionModal from "./SubscriptionModal";
import DeleteAccountModal from "./DeleteAccountModal";
import { formatPhoneDisplay } from "../../../utils/formatPhoneDisplay";

const groupClass =
  "overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

export default function Setting() {
  const navigate = useNavigate();
  const [showLogOutModal, setShowLogOutModal] = useState(false);
  const userData = useAppSelector((state) => state.userData);
  const translate = useTranslateText();
  const dispatch = useAppDispatch();

  const phone = formatPhoneDisplay(userData.country_code, userData.mobile_num);

  return (
    <>
      <BlockUserListModal />
      <SubscriptionModal />
      <LogoutModal isOpen={showLogOutModal} setIsOpen={setShowLogOutModal} />
      <DeleteAccountModal />

      <div className="flex h-[100dvh] w-full max-w-md flex-col overflow-hidden bg-secondary pb-[calc(3.25rem+env(safe-area-inset-bottom))] shadow-inner lg:pb-0">
        <div className="sticky top-0 z-20 shrink-0 border-b border-black/[0.06] bg-secondary/85 backdrop-blur-xl dark:border-white/[0.08]">
          <div className="flex items-center justify-between px-3 py-3">
            <button
              type="button"
              aria-label="Back"
              className="grid h-9 w-9 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10 lg:invisible"
              onClick={() => navigate(-1)}
            >
              <IoChevronBack className="text-xl" />
            </button>
            <span className="text-[17px] font-semibold tracking-tight text-darkText">
              <TextTranslate text="Settings" />
            </span>
            <div className="h-9 w-9" aria-hidden />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-4">
          {/* Hero */}
          <div className="mb-5 flex flex-col items-center text-center">
            <div className="relative mb-3">
              <img
                src={userData.profile_pic}
                className="h-[7.25rem] w-[7.25rem] rounded-full object-cover shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                alt=""
              />
              <button
                type="button"
                onClick={() =>
                  dispatch(updateViewState({ showChangeProfileModal: true }))
                }
                className="absolute bottom-0 right-0 grid h-8 w-8 place-content-center rounded-full bg-primary shadow ring-1 ring-black/10 dark:ring-white/15"
              >
                <FaPlus className="text-sm text-themeColor" />
              </button>
            </div>
            <div className="text-[1.375rem] font-semibold tracking-tight text-darkText">
              {userData.full_name}
            </div>
            {phone ? (
              <div className="mt-1 text-[13px] tabular-nums text-lightText">
                {phone}
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => navigate("/profile")}
              className="mt-2 text-[14px] font-medium text-themeColor"
            >
              <TextTranslate text="Profile" />
            </button>
          </div>

          <div className="space-y-3">
            <div className={`${groupClass} ${divide}`}>
              <ReusableProfileCard
                onClick={() =>
                  dispatch(
                    updateMessageOptions({ show_select_about_modal: true }),
                  )
                }
                icon={<BsInfoCircle className="text-lg" />}
                value={translate(userData.bio) || translate("About")}
                isDisabled={true}
              />
            </div>

            <div className={`${groupClass} ${divide}`}>
              <ReusableProfileCard
                onClick={() =>
                  dispatch(updateViewState({ show_select_theme_modal: true }))
                }
                icon={<MdOutlineColorLens className="text-xl" />}
                value={translate("Change Theme")}
                isDisabled={true}
              />
              <ThemeToggleFromSettings />
              <NotificationToggleFromSettings />
            </div>

            <div className={`${groupClass} ${divide}`}>
              <ReusableProfileCard
                onClick={() => {
                  dispatch(
                    updateMessageOptions({ show_all_star_messages: true }),
                  );
                  navigate("/star-messages");
                }}
                icon={<MdOutlineStarBorderPurple500 className="text-xl" />}
                value={translate("Starred Messages")}
                isDisabled={true}
              />
              <ReusableProfileCard
                onClick={() =>
                  dispatch(updateViewState({ showBlockUserList: true }))
                }
                icon={<RiUserUnfollowLine className="text-xl" />}
                value={translate("Block Contacts")}
                isDisabled={true}
              />
            </div>

            <div className={`${groupClass} ${divide}`}>
              <ReusableProfileCard
                onClick={() =>
                  dispatch(
                    updateViewState({
                      showPrivacyPolicy: true,
                      showTermsAndCondition: false,
                    }),
                  )
                }
                icon={<CiLock className="text-2xl" />}
                value={translate("Privacy Policy")}
                isDisabled={true}
              />
              <ReusableProfileCard
                onClick={() =>
                  dispatch(
                    updateViewState({
                      showTermsAndCondition: true,
                      showPrivacyPolicy: false,
                    }),
                  )
                }
                icon={<MdOutlinePrivacyTip className="text-xl" />}
                value={translate("Terms & Conditions")}
                isDisabled={true}
              />
              <RWebShare
                data={{
                  text: "Whoxa Chat",
                  url: `${window.location.origin}`,
                  title: "Whoxa Chat",
                }}
              >
                <div>
                  <ReusableProfileCard
                    icon={<RxShare2 className="text-lg" />}
                    value={translate("Share a link")}
                    isDisabled={true}
                  />
                </div>
              </RWebShare>
              <ReusableProfileCard
                onClick={() =>
                  dispatch(
                    updateViewState({ show_select_language_modal: true }),
                  )
                }
                icon={<HiLanguage className="text-xl" />}
                value={translate("Language")}
                isDisabled={true}
              />
            </div>

            <div className={groupClass}>
              <button
                type="button"
                onClick={() => setShowLogOutModal(true)}
                className="flex w-full items-center gap-3 px-4 py-3.5 text-[15px] text-[#FF3B30] active:bg-black/[0.04] dark:active:bg-white/[0.06]"
              >
                <HiOutlineLogout className="rotate-180 text-xl" />
                <TextTranslate text="Logout" />
              </button>
            </div>

            <button
              type="button"
              onClick={() =>
                dispatch(updateViewState({ showDeleteAccountModal: true }))
              }
              className="w-full py-2 text-center text-[15px] font-medium text-[#FF3B30]"
            >
              <TextTranslate text="Delete Account" />
            </button>

            <div className="pt-1 text-center text-[12px] text-lightText">
              <TextTranslate text="Version" /> 1.0.0
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
